import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  BarChart3, ArrowDownToLine, ArrowUpFromLine, Ban, CheckCircle, XCircle, Loader2, Eye, Filter,
} from "lucide-react";
import { toast } from "sonner";

export default function StatsPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("today");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [sspFilter, setSspFilter] = useState("all");
  const [dspFilter, setDspFilter] = useState("all");
  const [ssps, setSsps] = useState([]);
  const [dsps, setDsps] = useState([]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      let params = `period=${period}`;
      if (period === "custom" && customRange.start && customRange.end)
        params = `period=custom&start_date=${customRange.start}&end_date=${customRange.end}`;
      if (sspFilter !== "all") params += `&ssp_id=${sspFilter}`;
      if (dspFilter !== "all") params += `&dsp_id=${dspFilter}`;
      const r = await api.get(`/stats/summary?${params}`);
      setData(r.data);
      if (r.data.ssps) setSsps(r.data.ssps);
      if (r.data.dsps) setDsps(r.data.dsps);
    } catch {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const f = (n) => n == null ? "0" : Number(n).toLocaleString();
  const s = data?.summary || {};

  const REASON_LABELS = {
    ad_format_mismatch: "Ad Format Mismatch",
    country_blocked: "Country Blocked",
    os_blocked: "OS Blocked",
    ssp_blocked: "SSP Blocked",
    omsdk_missing: "OMSDK Missing",
    bidfloor_below_min: "Bidfloor Below Min",
    bidfloor_above_max: "Bidfloor Above Max",
    size_blocked: "Size Blocked",
    filter_list_blocked: "Filter List Blocked",
    traffic_type_blocked: "Traffic Type Blocked",
    adult_filter_blocked: "Adult Content Blocked",
    adult_filter_not_adult: "Non-Adult Content Blocked (Adult Only DSP)",
  };

  return (
    <div className="space-y-5" data-testid="stats-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <BarChart3 className="h-7 w-7 text-accent" /> Stats
        </h1>
        <p className="text-muted-foreground text-sm">Request flow, drop analysis, and bid response stats</p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="yesterday">Yesterday</SelectItem>
            <SelectItem value="7d">Last 7 Days</SelectItem>
            <SelectItem value="30d">Last 30 Days</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>
        {period === "custom" && (<>
          <Input type="date" value={customRange.start} onChange={e => setCustomRange({ ...customRange, start: e.target.value })} className="w-[140px] h-8 text-xs" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={customRange.end} min={customRange.start} onChange={e => setCustomRange({ ...customRange, end: e.target.value })} className="w-[140px] h-8 text-xs" />
        </>)}
        <Select value={sspFilter} onValueChange={setSspFilter}>
          <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="All SSPs" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All SSPs</SelectItem>
            {ssps.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dspFilter} onValueChange={setDspFilter}>
          <SelectTrigger className="w-[180px] h-8 text-xs"><SelectValue placeholder="All DSPs" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All DSPs</SelectItem>
            {dsps.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-8" onClick={fetchStats} disabled={loading} data-testid="stats-generate-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1" />}Generate
        </Button>
      </div>

      {data && (<>
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={ArrowDownToLine} label="Incoming" value={f(s.incoming)} color="text-blue-600" />
          <StatCard icon={Ban} label="Dropped" value={f(s.dropped)} sub={`${s.drop_rate || 0}%`} color="text-red-500" />
          <StatCard icon={ArrowUpFromLine} label="Outgoing" value={f(s.outgoing)} color="text-violet-600" />
          <StatCard icon={CheckCircle} label="Bid Responses" value={f(s.bid_responses)} color="text-emerald-600" />
          <StatCard icon={XCircle} label="No Bid" value={f(s.no_bid)} color="text-amber-500" />
          <StatCard icon={Filter} label="Fill Rate" value={`${s.fill_rate || 0}%`} color="text-cyan-600" />
        </div>

        {/* Drop Reasons */}
        {data.drop_reasons?.length > 0 && (
          <Card data-testid="drop-reasons-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><Ban className="h-4 w-4 text-red-500" /> Drop Reasons</CardTitle>
              <CardDescription>Why requests were dropped before reaching DSPs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Reason</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">% of Drops</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.drop_reasons.map((r, i) => {
                    const label = REASON_LABELS[r.reason] || r.reason;
                    const pct = s.dropped > 0 ? ((r.count / s.dropped) * 100).toFixed(1) : "0";
                    return (
                      <TableRow key={i}>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{label}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">{f(r.count)}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{pct}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Per-SSP Breakdown */}
        {data.ssp_breakdown?.length > 0 && (
          <Card data-testid="ssp-breakdown-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">SSP Breakdown</CardTitle>
              <CardDescription>Request flow per SSP endpoint</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SSP Endpoint</TableHead>
                    <TableHead className="text-right">Incoming</TableHead>
                    <TableHead className="text-right">Dropped</TableHead>
                    <TableHead className="text-right">Outgoing</TableHead>
                    <TableHead className="text-right">Bid Responses</TableHead>
                    <TableHead className="text-right">Fill Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.ssp_breakdown.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{r.ssp_name || r.ssp_id?.slice(0, 12)}</TableCell>
                      <TableCell className="text-right font-mono text-blue-600">{f(r.incoming)}</TableCell>
                      <TableCell className="text-right font-mono text-red-500">{f(r.dropped)}</TableCell>
                      <TableCell className="text-right font-mono text-violet-600">{f(r.outgoing)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{f(r.bid_responses)}</TableCell>
                      <TableCell className="text-right font-mono">{r.fill_rate}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </>)}

      {!data && !loading && (
        <Card className="py-16">
          <CardContent className="text-center text-muted-foreground">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">Click Generate to view stats</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="p-3" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${color}`} />
        <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
      </div>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}
